import * as React from 'react'

import type { SellerAttendanceRow } from '@sdr-agend/admin-schemas'

/**
 * Comparecimento por vendedor — barras de progresso (4.7c).
 *
 * Cada linha é uma barra fina: o preenchimento verde = % de comparecimento, o
 * fundo rose = no-show. Padrão "progress bar" moderno, compacto, em HTML/CSS
 * puro (sem SVG, sem lib de chart). O empty-state se auto-resolve.
 */
export interface AttendanceChartProps {
  data: ReadonlyArray<SellerAttendanceRow>
}

export function AttendanceChart({ data }: AttendanceChartProps): React.ReactElement {
  if (data.length === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="attendance-chart-empty"
      >
        Nenhum vendedor com reuniões finalizadas ainda.
      </div>
    )
  }

  return (
    <div data-testid="attendance-chart">
      <div className="space-y-2.5">
        {data.map((row) => {
          const total = row.attended + row.noShow
          const attendedPct = total === 0 ? 0 : (row.attended / total) * 100
          const ratePct =
            row.attendanceRate === null ? 'N/A' : `${Math.round(row.attendanceRate * 100)}%`
          const name =
            row.sellerName.length > 16 ? `${row.sellerName.slice(0, 15)}…` : row.sellerName

          return (
            <div key={row.sellerId} className="flex items-center gap-3">
              <span className="w-24 shrink-0 truncate text-xs font-medium" title={row.sellerName}>
                {name}
              </span>
              <div
                className="relative h-2 flex-1 overflow-hidden rounded-full bg-rose-400/80"
                role="img"
                aria-label={`${row.sellerName}: ${ratePct} de comparecimento`}
              >
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-emerald-500"
                  style={{ width: `${attendedPct}%` }}
                />
              </div>
              {/* Rótulo único (não dividir o "%" em nó próprio). */}
              <span className="w-[88px] shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {ratePct} ({row.attended}/{total})
              </span>
            </div>
          )
        })}
      </div>

      {/* Legenda */}
      <div className="mt-3 flex gap-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
          Compareceu
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-rose-400" />
          No-show
        </span>
      </div>
    </div>
  )
}

export default AttendanceChart
